import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getUser } from '../services/userServices'
import { useUserStore } from '../store/user'
import { User, UserQueryKeys } from '../types/user'
import { ClientMethodsTypes, client } from '@/services/v2/client'
import { ProfileQueryKeys } from 'containers/profile/types/query-keys'
import { getCookieToken } from '@/shared/utils'
import { useEffect } from 'react'
import { UserPreferences } from '@/interfaces/user'

const endpointUrl = '/api/v1/users'

export const useUser = () => {
  const { user, setUser } = useUserStore()
  const token = getCookieToken()

  const { isLoading, error, status, refetch, data } = useQuery({
    queryKey: [UserQueryKeys.USER],
    queryFn: async () => {
      return getUser()
    },
    enabled: !!token,
  })

  useEffect(() => {
    setUser(data)
  }, [data, setUser])

  return {
    user: user?.id ? user : data,
    isLoading: isLoading && !!token,
    error,
    status: token ? status : 'idle',
    isAuth: !!token && !error,
    refetch,
  }
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()
  const { user } = useUserStore()

  return useMutation({
    mutationFn: (userData: Partial<User>) => {
      return client<User>(endpointUrl, {
        method: ClientMethodsTypes.PATCH,
        body: {
          ...user,
          ...userData,
        },
      })
    },
    onSuccess: async (resonpose) => {
      const user = resonpose
      await Promise.allSettled([
        queryClient.invalidateQueries([
          ProfileQueryKeys.PROFILE,
          user?.username?.toLocaleLowerCase(),
        ]),
        queryClient.invalidateQueries([UserQueryKeys.USER]),
      ])
    },
  })
}

export const useUpdateUserPreferences = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (preferences: UserPreferences) => {
      return client<User>(`${endpointUrl}/update-preferences`, {
        method: ClientMethodsTypes.PATCH,
        body: {
          ...preferences,
        },
      })
    },
    onSuccess: async (resonpose) => {
      const user = resonpose
      await Promise.allSettled([
        queryClient.invalidateQueries([
          ProfileQueryKeys.PROFILE,
          user?.username?.toLocaleLowerCase(),
        ]),
        queryClient.invalidateQueries([UserQueryKeys.USER]),
      ])
    },
  })
}
